<template>
  <v-app :class="[languageClass, dynamicClass]">
    <core-header v-if="showHeader" />
    <SmartBannerMobile v-if="canSmartBannerMobileShow" />
    <Alert />
    <v-main :class="[bgColor, 'wd-pt-0']">
      <NotificationBar v-if="notifications.length > 0" key="宣傳 Bar" :notifications="notifications" />

      <v-container class="main__container" fluid>
        <slot />
      </v-container>
    </v-main>
    <core-footer v-if="showFooter" mx-auto />
    <client-only>
      <SmartBannerDesktop v-if="canSmartBannerDesktopShow" />
      <HeaderMenu v-if="showHeaderMenu" />
      <UserInfoNavigation v-if="showHeaderMenu" />
    </client-only>

    <root-message-dialog />

    <template v-if="shouldRootDynamicComponentShow">
      <Component :is="rootDynamicComponent" />
    </template>

    <json-ld-organization />
    <json-ld-website />
  </v-app>
</template>

<script>
import { mapGetters } from 'vuex'
import { useVuetify } from '@/composition/useUtil'
import { useBookingEngine } from '@/composition/use-booking-engine'
import CoreHeader from '@/components/core/Header.vue'
import CoreFooter from '@/components/core/Footer.vue'
import HeaderMenu from '@/components/navigation/header-menu.vue'
import Alert from '@/components/notice/alert.vue'
import UserInfoNavigation from '@/components/navigation/user-info-navigation.vue'
import RootMessageDialog from '@/components/dialog/root-message-dialog.vue'

import JsonLdOrganization from '@/components/jsonld/organization.vue'
import JsonLdWebsite from '@/components/jsonld/website.vue'

import { useIdentifyAndAttribution } from '@/composition/useGtm'
import { useHasSmartBannerClosedCookie } from '@/composition/useAppCookie'
import { useBreakpoints } from '@/composition/useBreakpoints'

import { usePlatformStore } from '@/store-pinia/platform'
import { useFunBookV2Store } from '@/store-pinia/funbookV2'
import { faviconUrl } from '@/view-models/common'
import { useGlobalStore } from '@/store-pinia/global'

const SmartBannerDesktop = defineAsyncComponent(() => import('@/components/SmartBannerDesktop.vue'))
const SmartBannerMobile = defineAsyncComponent(() => import('@/components/SmartBannerMobile.vue'))

export default {
  components: {
    NotificationBar: defineAsyncComponent(() => import('@/components/navigation/notification-bar.vue')),
    CoreHeader,
    CoreFooter,
    HeaderMenu,
    UserInfoNavigation,
    RootMessageDialog,
    SmartBannerDesktop,
    SmartBannerMobile,
    JsonLdOrganization,
    JsonLdWebsite,
    Alert,
  },
  setup() {
    const config = useRuntimeConfig()
    const nuxtApp = useNuxtApp()
    const vuetify = useVuetify()
    const { isMobile } = useBreakpoints()
    const i18n = useI18n()
    const i18nHead = useLocaleHead({ seo: true })
    const route = useRoute()
    const globalStore = useGlobalStore()
    const { isFunBookV2 } = storeToRefs(usePlatformStore())
    const { branchName, ogImage } = storeToRefs(useFunBookV2Store())
    const { rootDynamicComponent, shouldRootDynamicComponentShow } = storeToRefs(globalStore)

    const hasSmartBannerClosed = useHasSmartBannerClosedCookie()
    const { isBookingEngine } = useBookingEngine()
    useIdentifyAndAttribution(nuxtApp)
    useForeignDomain()

    const isXs = computed(() => vuetify.display.xs.value)
    const isAtFooterPage = computed(() => {
      const footerPagePaths = ['/contact', '/faq', '/terms/']
      return footerPagePaths.some(path => route.path.includes(path))
    })
    const dynamicClass = computed(() => {
      if (isAtFooterPage.value && !isFunBookV2.value) {
        return 'overlay-opacity-0'
      }

      return ''
    })

    const canSmartBannerDesktopShow = computed(() => !isXs.value && !hasSmartBannerClosed.value && !isFunBookV2.value && !isAtFooterPage.value)
    const canSmartBannerMobileShow = computed(
      () => !hasSmartBannerClosed.value && isMobile.value && !isBookingEngine.value && !isFunBookV2.value && !isAtFooterPage.value
    )

    const pageTitle = computed(() => (isFunBookV2.value ? `${branchName.value} | ${i18n.t('default_link_title_funbook')}` : i18n.t('funnow_title')))
    const currentPath = computed(() => `${config.public.BASE_URL}${route.path}`)
    const currentFullPath = computed(() => `${config.public.BASE_URL}${route.fullPath}`)
    const description = computed(() =>
      isFunBookV2.value ? i18n.t('default_link_meta_description_funbook', { branch_name: branchName.value }) : i18n.t('default_link_meta_description')
    )
    const i18nSeo = computed(() => getI18nSEO(i18nHead.value))
    const isOrderPages = computed(() => route.name?.includes('orders'))

    useHead(() => ({
      htmlAttrs: {
        lang: i18n.locale.value,
        ...i18nSeo.value.htmlAttrs,
      },
      title: pageTitle,
      meta: [
        { hid: 'title', name: 'title', content: pageTitle.value },
        { hid: 'description', name: 'description', content: description.value },
        { hid: 'og:title', property: 'og:title', content: pageTitle.value },
        { hid: 'og:description', property: 'og:description', content: description.value },
        { hid: 'og:image', property: 'og:image', content: ogImage.value },
        { property: 'fb:admins', content: 836889489 },
        { property: 'fb:app_id', content: config.public.FB_APP_ID },
        { property: 'og:url', content: currentPath.value },
        { property: 'al:web:url', content: currentPath.value },
        ...i18nSeo.value.meta,
      ],
      link: [{ rel: 'icon', type: 'image/x-icon', href: faviconUrl }].concat(
        isOrderPages.value ? [{ rel: 'canonical', href: currentFullPath.value }] : i18nSeo.value.link
      ),
    }))

    const bgColor = computed(() => (route.name?.startsWith('index__') || route.name?.startsWith('regions-rid___') ? 'wd-bg-white' : 'wd-bg-gray30'))

    return {
      dynamicClass,
      canSmartBannerDesktopShow,
      canSmartBannerMobileShow,
      isFunBookV2,
      branchName,
      ogImage,
      bgColor,
      shouldRootDynamicComponentShow,
      rootDynamicComponent,
      isOrderPages,
    }
  },
  computed: {
    languageClass() {
      return `language-${this.$i18n.locale}`
    },
    showHeader() {
      return !this.fromTngMiniProgram
    },
    showHeaderMenu() {
      return !(this.hideHeaderItems || this.foreignDomainFromCathay || this.foreignDomainFromMichelin)
    },
    showFooter() {
      return !this.foreignDomainFromMichelin && !this.hideFooter && !this.fromTngMiniProgram
    },
    ...mapGetters({
      foreignDomainFromMichelin: 'foreignDomainFromMichelin',
      foreignDomainFromCathay: 'foreignDomainFromCathay',
      notifications: 'notification/notifications',
      hideHeaderItems: 'hideHeaderItems',
      hideFooter: 'hideFooter',
      fromTngMiniProgram: 'fromTngMiniProgram',
    }),
  },
  mounted() {
    if (this.foreignDomainFromMichelin) {
      window.sessionStorage.setItem('3rd_Source', 'Michelin')
    }

    if (this.foreignDomainFromCathay) {
      window.sessionStorage.setItem('3rd_Source', 'Cathay')
    }
  },
}
</script>

<style lang="scss" src="@/assets/sass/pages/index.scss"></style>
